import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { dataApi, uploadMedia } from '@/lib/api';
import { isNative } from '@/lib/platform';
import { startStt, type SttSession } from '@/lib/voice';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { useI18n } from '@/lib/i18n';
import { Icon } from './Icon';
import { MediaAttachments } from './MediaAttachments';
import type { AssistantDraft, AssistantMessage } from '@/types/api';

export function AssistantPanel({
  familyId,
  initialFiles,
  onInitialFilesConsumed,
  onClose,
}: {
  familyId: string;
  initialFiles?: File[];
  onInitialFilesConsumed?: () => void;
  onClose: () => void;
}) {
  const { t, locale } = useI18n();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyDraft, setBusyDraft] = useState<string | null>(null);
  const [appliedLink, setAppliedLink] = useState<string | null>(() =>
    sessionStorage.getItem(`heritage.assistant.lastLink.${familyId}`),
  );
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<Array<{ id: string; name: string }>>([]);
  const [uploading, setUploading] = useState(false);
  const [listening, setListening] = useState(false);
  const sttRef = useRef<SttSession | null>(null);
  const endRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const cameraInputRef = useRef<HTMLInputElement | null>(null);
  const wasBusyRef = useRef(false);

  const state = useQuery({
    queryKey: ['assistant-state', familyId],
    queryFn: () => dataApi.assistantState(familyId),
    refetchInterval: (query) => (query.state.data?.busy ? 2000 : 10000),
  });

  const status = useQuery({
    queryKey: ['assistant-status', familyId],
    queryFn: () => dataApi.assistantStatus(familyId),
    refetchInterval: (query) => (query.state.data?.busy ? 1500 : 5000),
  });

  const messages = useQuery({
    queryKey: ['assistant-messages', familyId],
    queryFn: () => dataApi.assistantMessages(familyId),
    refetchInterval: status.data?.busy ? 1500 : false,
  });

  const busy = Boolean(status.data?.busy ?? state.data?.busy);
  const drafts = state.data?.drafts ?? [];

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' });
  }, [messages.data?.length, busy]);

  useEffect(() => {
    return () => {
      sttRef.current?.cancel();
      sttRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!initialFiles || initialFiles.length === 0) return;
    addFiles(initialFiles);
    onInitialFilesConsumed?.();
  }, [initialFiles, onInitialFilesConsumed]);

  useEffect(() => {
    if (wasBusyRef.current && !busy) {
      void queryClient.invalidateQueries({
        queryKey: ['assistant-messages', familyId],
      });
      void queryClient.invalidateQueries({
        queryKey: ['assistant-state', familyId],
      });
    }
    wasBusyRef.current = busy;
  }, [busy, familyId, queryClient]);

  async function send() {
    const value = text.trim();
    if (!value || sending || busy || uploading) return;
    setSending(true);
    setError(null);
    try {
      let nextUploaded = [...uploadedFiles];
      if (pendingFiles.length > 0) {
        setUploading(true);
        try {
          for (const file of pendingFiles) {
            const asset = await uploadMedia(familyId, file);
            nextUploaded.push({
              id: asset.id,
              name: asset.originalName ?? file.name,
            });
          }
          setUploadedFiles(nextUploaded);
          setPendingFiles([]);
        } finally {
          setUploading(false);
        }
      }
      await dataApi.assistantSend(
        familyId,
        value,
        nextUploaded.map((file) => file.id),
      );
      setText('');
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['assistant-messages', familyId],
        }),
        queryClient.invalidateQueries({
          queryKey: ['assistant-status', familyId],
        }),
        queryClient.invalidateQueries({
          queryKey: ['assistant-state', familyId],
        }),
      ]);
    } catch (err) {
      setError(err instanceof Error ? t(err.message) : t('Ошибка ИИ'));
    } finally {
      setSending(false);
    }
  }

  function addFiles(incoming: FileList | File[]) {
    const files = Array.from(incoming);
    if (files.length === 0) return;
    setError(null);
    const remaining = 5 - pendingFiles.length - uploadedFiles.length;
    if (remaining <= 0) {
      setError(t('Можно прикрепить не больше 5 файлов.'));
      return;
    }
    const accepted: File[] = [];
    for (const file of files.slice(0, remaining)) {
      if (file.size > 15 * 1024 * 1024) {
        setError(t('Файл больше 15 МБ.'));
        continue;
      }
      accepted.push(file);
    }
    if (accepted.length > 0) {
      setPendingFiles((current) => [...current, ...accepted]);
    }
  }

  async function takePhoto() {
    if (!isNative()) {
      cameraInputRef.current?.click();
      return;
    }
    try {
      const photo = await Camera.getPhoto({
        resultType: CameraResultType.Uri,
        source: CameraSource.Camera,
        quality: 85,
      });
      if (!photo.webPath) return;
      const blob = await fetch(photo.webPath).then((response) => response.blob());
      const format = photo.format || 'jpeg';
      addFiles([
        new File([blob], `photo.${format}`, {
          type: blob.type || `image/${format}`,
        }),
      ]);
    } catch {
      setError(t('Не удалось сделать фото.'));
    }
  }

  function removePending(index: number) {
    setPendingFiles((current) => current.filter((_, i) => i !== index));
  }

  function removeUploaded(index: number) {
    setUploadedFiles((current) => current.filter((_, i) => i !== index));
  }

  async function toggleMic() {
    if (sttRef.current) {
      sttRef.current.stop();
      sttRef.current = null;
      setListening(false);
      return;
    }
    if (sending || uploading || busy) return;
    const baseText = text.trim();
    try {
      const session = await startStt(
        {
          onPartial: (value) => {
            setText(baseText ? `${baseText} ${value}` : value);
          },
          onFinal: (value) => {
            setText(baseText ? `${baseText} ${value}` : value);
          },
          onError: (message) => {
            setError(t(message));
          },
          onEnd: () => {
            sttRef.current = null;
            setListening(false);
          },
        },
        {
          familyId,
          language: locale === 'en' ? 'en' : 'ru',
        },
      );
      sttRef.current = session;
      setListening(true);
    } catch (error) {
      setError(
        error instanceof Error ? t(error.message) : t('Микрофон недоступен'),
      );
      setListening(false);
    }
  }

  async function stop() {
    await dataApi.assistantAbort(familyId).catch(() => undefined);
    await queryClient.invalidateQueries({
      queryKey: ['assistant-status', familyId],
    });
  }

  async function applyDraft(draft: AssistantDraft) {
    setBusyDraft(draft.id);
    setError(null);
    try {
      const result = await dataApi.assistantApplyDraft(familyId, draft.id);
      setAppliedLink(result.viewPath);
      if (result.viewPath) {
        sessionStorage.setItem(
          `heritage.assistant.lastLink.${familyId}`,
          result.viewPath,
        );
      }
      setUploadedFiles([]);
      setPendingFiles([]);
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['assistant-state', familyId],
        }),
        queryClient.invalidateQueries({
          queryKey: ['assistant-messages', familyId],
        }),
        queryClient.invalidateQueries({
          queryKey: ['assistant-status', familyId],
        }),
        queryClient.invalidateQueries({ queryKey: ['tree', familyId] }),
        queryClient.invalidateQueries({ queryKey: ['facts', familyId] }),
        queryClient.invalidateQueries({ queryKey: ['map-facts', familyId] }),
        queryClient.invalidateQueries({ queryKey: ['person'] }),
      ]);
    } catch (err) {
      setError(err instanceof Error ? t(err.message) : t('Не удалось применить'));
    } finally {
      setBusyDraft(null);
    }
  }

  async function cancelDraft(draft: AssistantDraft) {
    setBusyDraft(draft.id);
    setError(null);
    try {
      await dataApi.assistantCancelDraft(familyId, draft.id);
      await queryClient.invalidateQueries({
        queryKey: ['assistant-state', familyId],
      });
    } catch (err) {
      setError(err instanceof Error ? t(err.message) : t('Не удалось отменить'));
    } finally {
      setBusyDraft(null);
    }
  }

  return (
    <div className="fixed inset-0 z-[1600] flex items-end justify-end bg-black/30 p-0 sm:items-center sm:p-4">
      <section className="flex h-[88dvh] w-full flex-col overflow-hidden rounded-t-2xl bg-surface shadow-2xl sm:h-[min(760px,88dvh)] sm:max-w-lg sm:rounded-2xl">
        <header className="flex items-center justify-between gap-3 border-b border-heritage-100 px-4 py-3">
          <div className="flex min-w-0 items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-heritage-100 text-heritage-700">
              <Icon name="sparkles" className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <h2 className="truncate font-semibold">{t('ИИ-ассистент')}</h2>
              <p className="truncate text-xs text-heritage-500">
                {t('Помогает с персонами и дневником')}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="btn-secondary px-3 py-1.5 text-xs"
          >
            {t('Закрыть')}
          </button>
        </header>

        <div className="min-w-0 flex-1 space-y-3 overflow-y-auto overflow-x-hidden px-4 py-3">
          {messages.isLoading && (
            <p className="text-sm text-heritage-500">{t('Загрузка…')}</p>
          )}
          {!messages.isLoading && (messages.data?.length ?? 0) === 0 && (
            <div className="rounded-xl bg-heritage-50 p-3 text-sm text-heritage-600">
              <p className="font-medium">{t('Что можно сделать?')}</p>
              <p className="mt-1 text-xs">
                {t(
                  'Попросите добавить или исправить информацию о персоне или записи дневника. Любое изменение сначала появится черновиком и потребует подтверждения.',
                )}
              </p>
            </div>
          )}
          {messages.data?.map((message: AssistantMessage) => (
            <MessageBubble
              key={message.id}
              familyId={familyId}
              message={message}
              onInternalLink={onClose}
            />
          ))}
          {busy && (
            <p className="text-xs text-heritage-500">{t('ИИ думает…')}</p>
          )}
          <div ref={endRef} />
        </div>

        {drafts.length > 0 && (
          <div className="max-h-[34dvh] space-y-2 overflow-y-auto border-t border-heritage-100 px-4 py-3">
            {drafts.map((draft) => (
              <div
                key={draft.id}
                className="rounded-xl border border-amber-200 bg-amber-50 p-3"
              >
                <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">
                  {t('Черновик изменения')}
                </p>
                <p className="mt-1 text-sm font-semibold text-heritage-800">
                  {t(draft.preview.title)}
                </p>
                {draft.preview.fields.length > 0 ? (
                  <dl className="mt-2 grid gap-x-4 gap-y-1.5 text-sm sm:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)]">
                    {draft.preview.fields.map((field, index) => (
                      <div key={`${field.label}-${index}`} className="contents">
                        <dt className="text-heritage-500">{t(field.label)}</dt>
                        <dd className="whitespace-pre-wrap break-words text-heritage-800">
                          {field.value}
                        </dd>
                      </div>
                    ))}
                  </dl>
                ) : (
                  <p className="mt-1 text-sm text-heritage-500">
                    {draft.summary}
                  </p>
                )}
                <div className="mt-2 flex gap-2">
                  <button
                    type="button"
                    onClick={() => void applyDraft(draft)}
                    disabled={busyDraft === draft.id}
                    className="btn-primary px-3 py-1.5 text-xs"
                  >
                    {t('Подтвердить')}
                  </button>
                  <button
                    type="button"
                    onClick={() => void cancelDraft(draft)}
                    disabled={busyDraft === draft.id}
                    className="btn-secondary px-3 py-1.5 text-xs"
                  >
                    {t('Отменить')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {appliedLink && (
          <div className="mx-4 mb-2 flex flex-wrap items-center justify-between gap-2 rounded-xl bg-green-50 px-3 py-2 text-xs text-green-800">
            <span>{t('Изменение применено')}</span>
            <button
              type="button"
              onClick={() => {
                navigate(appliedLink);
                sessionStorage.removeItem(
                  `heritage.assistant.lastLink.${familyId}`,
                );
                onClose();
              }}
              className="font-medium underline"
            >
              {appliedLink.includes('/diary')
                ? t('Открыть запись')
                : t('Открыть')}
            </button>
          </div>
        )}

        {error && (
          <p className="mx-4 mb-2 rounded-xl bg-red-50 px-3 py-2 text-xs text-red-700">
            {error}
          </p>
        )}

        <footer className="border-t border-heritage-100 p-3">
          {(pendingFiles.length > 0 || uploadedFiles.length > 0) && (
            <div className="mb-2 flex flex-wrap gap-1.5">
              {uploadedFiles.map((file, index) => (
                <span
                  key={file.id}
                  className="inline-flex max-w-full items-center gap-1 rounded-full bg-heritage-100 px-2 py-1 text-[11px] text-heritage-700"
                >
                  <span className="truncate">{file.name}</span>
                  <button
                    type="button"
                    onClick={() => removeUploaded(index)}
                    className="text-heritage-500 hover:text-red-600"
                    aria-label={t('Удалить файл')}
                  >
                    ×
                  </button>
                </span>
              ))}
              {pendingFiles.map((file, index) => (
                <span
                  key={`${file.name}-${file.lastModified}-${index}`}
                  className="inline-flex max-w-full items-center gap-1 rounded-full bg-heritage-50 px-2 py-1 text-[11px] text-heritage-600"
                >
                  <span className="truncate">{file.name}</span>
                  <button
                    type="button"
                    onClick={() => removePending(index)}
                    className="text-heritage-500 hover:text-red-600"
                    aria-label={t('Удалить файл')}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
          <textarea
            value={text}
            onChange={(event) => setText(event.target.value)}
            rows={2}
            className="input resize-none text-sm"
            placeholder={t('Например: прикрепи это фото к поездке в Москву 2025 года')}
            onKeyDown={(event) => {
              if (
                event.key === 'Enter' &&
                !event.shiftKey &&
                !event.nativeEvent.isComposing
              ) {
                event.preventDefault();
                void send();
              }
            }}
          />
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,.pdf,.txt,.doc,.docx,.xls,.xlsx"
            className="hidden"
            onChange={(event) => {
              if (event.currentTarget.files) addFiles(event.currentTarget.files);
              event.currentTarget.value = '';
            }}
          />
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={(event) => {
              if (event.currentTarget.files) addFiles(event.currentTarget.files);
              event.currentTarget.value = '';
            }}
          />
          <div className="mt-2 flex items-center justify-between gap-2">
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => void toggleMic()}
                disabled={uploading || sending || busy}
                className={`flex h-9 w-9 items-center justify-center rounded-full hover:bg-heritage-100 disabled:opacity-40 ${
                  listening ? 'bg-red-100 text-red-700' : 'text-heritage-500'
                }`}
                aria-label={listening ? t('Остановить запись') : t('Голосовой ввод')}
                title={listening ? t('Остановить запись') : t('Голосовой ввод')}
              >
                <Icon name="mic" className="h-5 w-5" />
              </button>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading || sending}
                className="flex h-9 w-9 items-center justify-center rounded-full text-heritage-500 hover:bg-heritage-100 disabled:opacity-40"
                aria-label={t('Прикрепить файл')}
                title={t('Прикрепить файл')}
              >
                <Icon name="paperclip" className="h-5 w-5" />
              </button>
              <button
                type="button"
                onClick={() => void takePhoto()}
                disabled={uploading || sending}
                className="flex h-9 w-9 items-center justify-center rounded-full text-heritage-500 hover:bg-heritage-100 disabled:opacity-40"
                aria-label={t('Сделать фото')}
                title={t('Сделать фото')}
              >
                <Icon name="camera" className="h-5 w-5" />
              </button>
              {listening && (
                <span className="text-[10px] text-red-600">
                  {t('Слушаю…')}
                </span>
              )}
              {uploading && (
                <span className="text-[10px] text-heritage-400">
                  {t('Загрузка файлов…')}
                </span>
              )}
            </div>
            {busy ? (
              <button
                type="button"
                onClick={() => void stop()}
                className="btn-secondary px-3 py-1.5 text-xs"
              >
                {t('Остановить')}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => void send()}
                disabled={sending || uploading || !text.trim()}
                className="btn-primary px-3 py-1.5 text-xs"
              >
                {sending ? t('Отправка…') : t('Отправить')}
              </button>
            )}
          </div>
        </footer>
      </section>
    </div>
  );
}

function MessageText({
  text,
  onInternalLink,
}: {
  text: string;
  onInternalLink?: () => void;
}) {
  const parts = text.split(/(https?:\/\/[^\s]+|\/families\/[^\s]+)/g);
  return (
    <>
      {parts.map((part, index) => {
        const trimmed = part.replace(/[.,);]+$/, '');
        const suffix = part.slice(trimmed.length);
        const internal = trimmed
          .replace(/^https?:\/\/[^/]+/, '')
          .startsWith('/families/')
          ? trimmed.replace(/^https?:\/\/[^/]+/, '')
          : trimmed.startsWith('/families/')
            ? trimmed
            : null;
        if (internal) {
          return (
            <span key={index}>
              <Link
                to={internal}
                onClick={onInternalLink}
                className="break-all underline"
              >
                {trimmed}
              </Link>
              {suffix}
            </span>
          );
        }
        if (/^https?:\/\//.test(trimmed)) {
          return (
            <span key={index}>
              <a
                href={trimmed}
                target="_blank"
                rel="noreferrer"
                className="break-all underline"
              >
                {trimmed}
              </a>
              {suffix}
            </span>
          );
        }
        return <span key={index}>{part}</span>;
      })}
    </>
  );
}

function MessageBubble({
  familyId,
  message,
  onInternalLink,
}: {
  familyId: string;
  message: AssistantMessage;
  onInternalLink?: () => void;
}) {
  const { t } = useI18n();
  const own = message.role === 'user';
  const content =
    message.text ||
    (message.tools.length > 0
      ? t('Обновляю данные…')
      : t('Пустой ответ'));
  return (
    <div className={own ? 'flex justify-end' : 'flex justify-start'}>
      <div
        className={`min-w-0 max-w-[85%] whitespace-pre-wrap break-words rounded-2xl px-3 py-2 text-sm [overflow-wrap:anywhere] ${
          own
            ? 'bg-heritage-600 text-white'
            : 'bg-heritage-50 text-heritage-800'
        }`}
      >
        <MessageText text={content} onInternalLink={onInternalLink} />
        {message.attachments && message.attachments.length > 0 && (
          <MediaAttachments
            familyId={familyId}
            ids={message.attachments.map((attachment) => attachment.id)}
            light={own}
            horizontal
          />
        )}
      </div>
    </div>
  );
}
